import React, { useState } from 'react';
import { Box, Container, Stepper, Step, StepLabel, Paper, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

import PickupDetails from './steps/PickupDetails';
import VehicleSelection from './steps/VehicleSelection';
import CustomerDetails from './steps/CustomerDetails';
import PaymentConfirmation from './steps/PaymentConfirmation';
import BookingConfirmation from './steps/BookingConfirmation';

const steps = [
  'Pickup',
  'Vehicle',
  'Details',
  'Payment',
  'Confirmed'
];

const BookingFlow = ({ variant = 'default' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeStep, setActiveStep] = useState(0);
  const [bookingData, setBookingData] = useState({
    pickup: {
      location: '',
      dropoff: '',
      dateTime: null,
      passengers: 1,
      duration: '1',
      locationCoords: null,
      dropoffCoords: null,
      hasReturnJourney: false,
      returnLocation: '',
      returnDropoff: '',
      returnDateTime: null,
      returnLocationCoords: null,
      returnDropoffCoords: null,
      discountCode: ''
    },
    vehicle: {
      name: '',
      type: '',
      capacity: 0,
      features: [],
      images: []
    },
    customer: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      notes: ''
    },
    payment: {
      method: '',
      coupon: '',
      termsAccepted: false
    },
    confirmationMessage: ''
  });

  const handleNext = (stepData) => {
    setBookingData(prev => {
      const newState = { ...prev, ...stepData };
      console.log('BookingFlow state updated:', newState);
      return newState;
    });
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const StepContent = () => {
    switch (activeStep) {
      case 0:
        return <PickupDetails data={bookingData.pickup} onNext={handleNext} />;
      case 1:
        return <VehicleSelection 
          data={bookingData.vehicle} 
          bookingData={bookingData}
          onNext={handleNext} 
          onBack={handleBack} 
        />;
      case 2:
        return <CustomerDetails 
          data={bookingData.customer} 
          bookingSummary={bookingData} 
          onNext={handleNext} 
          onBack={handleBack} 
        />;
      case 3:
        return <PaymentConfirmation 
          bookingSummary={bookingData}
          onNext={handleNext} 
          onBack={handleBack} 
        />;
      case 4:
        return <BookingConfirmation bookingData={bookingData} />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stepper 
        activeStep={activeStep} 
        alternativeLabel
        sx={{ 
          mb: { xs: 2, md: 4 },
          '& .MuiStepLabel-label': {
            fontSize: { xs: '0.7rem', md: '0.875rem' },
            mt: { xs: 0.5, md: 1 },
            whiteSpace: 'nowrap',
          },
          '& .MuiStepIcon-root': {
            fontSize: { xs: '1.25rem', md: '1.5rem' },
          },
          '& .MuiStep-root': {
            px: { xs: 0.5, md: 1 },
          },
          '& .MuiStepConnector-line': {
            minHeight: { xs: 1, md: 3 },
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <AnimatePresence mode="wait">
        <motion.div
          key={activeStep}
          initial={{ opacity: 0, x: 10 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -10 }}
          transition={{ duration: 0.2 }}
        >
          <StepContent />
        </motion.div>
      </AnimatePresence>
    </Box>
  );
};

export default BookingFlow; 